// Ticket detail — route /tickets/:publicRef.
//
// `publicRef` is the customer-visible ticket number (`tickets.public_ref`), not
// `tickets.id`: every ticket URL in the app is keyed on it so Zendesk references
// carried over in the migration still resolve (docs/API.md §3.2).
//
// One GET returns everything this screen renders — messages, events, CCs, followers,
// parent and children, custom fields — so there is a single load and a single error
// state for the whole page.
//
// The thing that must not be got wrong here is the internal-note distinction; it lives
// in MessageThread.jsx and ReplyComposer.jsx, with the reasoning written out there.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { api } from '../../api/client.js';
import { useLiveRefresh } from '../../hooks/useLiveRefresh.js';
import MessageThread from '../../components/tickets/MessageThread.jsx';
import ReplyComposer from '../../components/tickets/ReplyComposer.jsx';
import TicketMeta from '../../components/tickets/TicketMeta.jsx';
import TicketEditPanel from '../../components/tickets/TicketEditPanel.jsx';
import ActivityLog from '../../components/tickets/ActivityLog.jsx';
import RelatedTickets from '../../components/tickets/RelatedTickets.jsx';
import { useCanWrite } from '../../session/SessionProvider.jsx';
import { StatusBadge, PriorityLabel, SlaFlag } from '../../components/tickets/Badges.jsx';
import { CHANNEL_LABELS, formatDateTime, formatRelative } from '../../components/tickets/format.js';

function Panel({ title, children, action }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-10 text-center">
      <h1 className="text-base font-semibold text-ink">{title}</h1>
      <p className="mt-1 text-sm text-ink-muted">{children}</p>
      {action}
    </div>
  );
}

export default function TicketDetail() {
  const { publicRef } = useParams();

  const [state, setState] = useState({ status: 'loading', ticket: null, error: null });
  // Both of these only put names on ids that the ticket carries as raw text. They are
  // deliberately fail-soft: the screen is fully usable without either, and neither is
  // worth an error state on the highest-value page in the app.
  const [users, setUsers] = useState([]);
  const [namedStatuses, setNamedStatuses] = useState([]);
  const [editing, setEditing] = useState(false);
  const canWrite = useCanWrite();

  const load = useCallback(
    (signal) =>
      api
        .get(`/tickets/${encodeURIComponent(publicRef)}`, { signal })
        .then((ticket) => setState({ status: 'ready', ticket, error: null }))
        .catch((error) => {
          if (signal?.aborted || error.name === 'AbortError') return;
          setState({ status: 'error', ticket: null, error });
        }),
    [publicRef],
  );

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', ticket: null, error: null });
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    const controller = new AbortController();
    // No capability filter: anyone can be mentioned or followed, viewers included —
    // that is the point of cross-team visibility (docs/API.md §5.5).
    api.get('/users', { signal: controller.signal })
      .then((payload) => setUsers(payload?.data ?? []))
      .catch(() => {});
    api.get('/ticket-statuses?include_inactive=true', { signal: controller.signal })
      .then((payload) => setNamedStatuses(payload?.data ?? []))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const ticket = state.ticket;

  const usersById = useMemo(() => {
    const map = new Map();
    // Seeded from the ticket first so assignee and follower names still resolve if the
    // directory call failed, then overlaid with the full list.
    for (const message of ticket?.messages ?? []) {
      if (message.author_type === 'user' && message.author) {
        map.set(message.author.id, message.author.display_name);
      }
    }
    for (const follower of ticket?.followers ?? []) {
      map.set(follower.user.id, follower.user.display_name);
    }
    if (ticket?.assignee) map.set(ticket.assignee.id, ticket.assignee.display_name);
    if (ticket?.created_by) map.set(ticket.created_by.id, ticket.created_by.display_name);
    for (const user of users) map.set(user.id, user.display_name);
    return map;
  }, [ticket, users]);

  const customersById = useMemo(() => {
    const map = new Map();
    if (ticket?.customer) map.set(ticket.customer.id, ticket.customer.name);
    for (const cc of ticket?.ccs ?? []) map.set(cc.customer.id, cc.customer.name);
    return map;
  }, [ticket]);

  const namedStatusesById = useMemo(
    () => new Map(namedStatuses.map((status) => [status.id, status.name])),
    [namedStatuses],
  );

  /**
   * A reload that cannot cost the reader what is already on screen.
   *
   * `load` replaces the whole state, and its failure branch blanks the ticket and
   * shows the error page. That is right for the FIRST load — there is nothing to
   * lose and nothing to show — and wrong for every refresh after it: a transient
   * 502 on a background poll would take away the ticket the agent is reading, and
   * the reply they just posted with it. So a refresh that fails leaves the screen
   * exactly as it was and waits for the next one.
   */
  const refresh = useCallback(
    (signal) =>
      api
        .get(`/tickets/${encodeURIComponent(publicRef)}`, { signal })
        .then((ticket) => setState({ status: 'ready', ticket, error: null }))
        .catch(() => {}),
    [publicRef],
  );

  /**
   * The POST returns the created message in thread shape, so it is appended straight
   * away and the agent sees their reply the moment it lands.
   *
   * Then a refresh, ALWAYS — not only when the status changed. The append covers
   * `messages` and nothing else, but posting also writes a `message_added` row to
   * `ticket_events`, which is what the activity log at the foot of the page renders.
   * Without this the log stayed one entry behind until the agent reloaded by hand,
   * and it was the agent's OWN note that was missing from it — the change they had
   * the most reason to expect to see.
   *
   * Both, rather than only the refetch: the optimistic append is what makes posting
   * feel immediate, and the refetch is what makes the rest of the page true.
   */
  const handlePosted = useCallback(
    (message) => {
      setState((previous) =>
        previous.ticket
          ? {
              ...previous,
              ticket: { ...previous.ticket, messages: [...previous.ticket.messages, message] },
            }
          : previous,
      );
      void refresh();
    },
    [refresh],
  );

  // A colleague's reply, a status change, or an emailed follow-up from the customer
  // lands on this page without the agent reloading it. `refresh` and not `load`: a
  // poll that fails must not replace the ticket being read with an error page.
  //
  // Held off until the first load has succeeded. Polling behind the loading panel
  // would race the initial request for no benefit, and polling behind the ERROR panel
  // would silently repair a screen that is telling the agent something is wrong —
  // the retry there belongs to the agent, not to a timer.
  // PAUSED WHILE EDITING, and this is not politeness about a moving screen.
  //
  // `TicketEditPanel` diffs its draft against the ticket it was handed, so a poll that
  // replaced `ticket` mid-edit would move the BASELINE while the draft stayed put: a
  // field a colleague had just changed would start reading as one this agent had
  // changed, and saving would silently write the stale value back over them. The panel
  // refetches when it saves, so nothing is missed by holding still until then.
  useLiveRefresh(refresh, { enabled: state.status === 'ready' && !editing });

  if (state.status === 'loading') {
    return <Panel title={`Ticket #${publicRef}`}>Loading…</Panel>;
  }

  if (state.status === 'error') {
    const notFound = state.error?.code === 'TICKET_NOT_FOUND' || state.error?.status === 404;
    return (
      <Panel
        title={notFound ? `No ticket #${publicRef}` : 'Could not load this ticket'}
        action={
          <Link to="/tickets" className="btn-ghost mt-4 ring-1 ring-line-strong">
            Back to the queue
          </Link>
        }
      >
        {notFound
          ? 'The ticket number may be mistyped, or the ticket was never migrated.'
          : state.error?.message}
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        {/* brand-700 reads as a link hover on white but sinks into a dark page, so
            the hover takes a real dark step. Everything else here is a token. */}
        <Link
          to="/tickets"
          className="text-sm text-ink-muted hover:text-brand-700 dark:hover:text-brand-300"
        >
          ← Tickets
        </Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-ink">
            <span className="mr-2 font-mono text-base font-normal text-ink-muted">
              #{ticket.public_ref}
            </span>
            {ticket.subject}
          </h1>
          <div className="flex shrink-0 items-center gap-3">
            <span
              className="text-xs text-ink-muted"
              title={formatDateTime(ticket.updated_at) ?? undefined}
            >
              Updated {formatRelative(ticket.updated_at)}
            </span>
            {!editing && (
              <button
                type="button"
                className="btn-ghost ring-1 ring-line-strong"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusBadge status={ticket.status} namedStatus={ticket.named_status} />
          <PriorityLabel priority={ticket.priority} />
          <span className="text-sm text-ink-muted">·</span>
          <span className="text-sm text-ink-secondary">
            {CHANNEL_LABELS[ticket.channel] ?? ticket.channel}
          </span>
          <SlaFlag breached={ticket.sla_breached} dueAt={ticket.sla_first_response_due_at} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* `public_ref` is passed down because an attachment's download URL is
              keyed on the TICKET, not on the attachment alone — the server reaches
              the row through the ticket to decide whether this caller may have it
              (src/routes/api/tickets.js). The thread cannot build the URL without it. */}
          <MessageThread messages={ticket.messages} publicRef={ticket.public_ref} />

          <ReplyComposer
            publicRef={ticket.public_ref}
            customer={ticket.customer}
            ccs={ticket.ccs}
            users={users}
            onPosted={handlePosted}
          />

          <ActivityLog
            events={ticket.events}
            usersById={usersById}
            customersById={customersById}
            namedStatusesById={namedStatusesById}
          />
        </div>

        {/* The editor REPLACES the read-only sidebar rather than sitting beside it, so
            there is never a second copy of the same fields on screen disagreeing with
            what is being typed. */}
        <div className="space-y-4">
          {editing ? (
            <TicketEditPanel
              ticket={ticket}
              onCancel={() => setEditing(false)}
              onSaved={(updated) => {
                // PATCH answers in the §3.2 detail shape — the same shape this page
                // loaded — so the response can be used directly rather than refetched.
                // That is not true of the customer panel next door, whose endpoint
                // answers in the flatter directory shape.
                setState({ status: 'ready', ticket: updated, error: null });
                setEditing(false);
              }}
            />
          ) : (
            <TicketMeta ticket={ticket} />
          )}

          {/* Parent, children and related tickets — with the (+) that spawns a child and
              the box that links another ticket. `refresh`, not `load`: a link that lands
              must not be able to blank the page if the re-read hiccups. */}
          <RelatedTickets ticket={ticket} canWrite={canWrite} onChanged={() => refresh()} />
        </div>
      </div>
    </div>
  );
}
